'use client';

import { createContext, ReactNode, useContext, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { ServiceType } from '@/creditflowresearch/domain/ServiceType';
import ContactRepository from '@/creditflowresearch/infrastructure/repository/ContactRepository';
import { getContainer } from '@framework/di/getContainer';
import messages from '@framework/message/messages';
import { IRequestServicePayload } from '@/creditflowresearch/domain/vo/IRequestServicePayload';
import { AlertManager } from '@creditflowresearch/uilib/Alert';
import { getErrorMessage } from '@framework/message/Mapper/getMessage';
import { IRequestServiceData } from '@/creditflowresearch/ui/common/components/RequestServiceModal/IRequestServiceData';
import config from './modalConfig';

interface IRequestServiceModalState {
  isOpen: boolean,
  titleModal: ReactNode,
  titleForm: ReactNode,
  commentPlaceholder?: string,
  recaptcha: ReCAPTCHA | null,
  serviceType: ServiceType | null,
  isCaptcha: boolean,
  isSolutionsSelect: boolean,
  isFetching: boolean,
}

const INITIAL_STATE: IRequestServiceModalState = {
  isOpen: false,
  titleModal: '',
  titleForm: '',
  recaptcha: null,
  serviceType: null,
  isCaptcha: true,
  isSolutionsSelect: false,
  isFetching: false,
};

const useRequestServiceModal = () => {
  const [state, setState] = useState<IRequestServiceModalState>(INITIAL_STATE);
  const container = getContainer();
  const contactRepository = container.get(ContactRepository);

  const openModal = (serviceType: ServiceType) => {
    setState((prevState) => ({
      ...prevState, isOpen: true, serviceType, ...config[serviceType],
    }));
  };

  const closeModal = () => {
    setState(INITIAL_STATE);
  };

  const setRecaptchaObject = (recaptcha: ReCAPTCHA) => {
    setState((prevState) => ({ ...prevState, recaptcha }));
  };

  const requestService = async (payload: IRequestServicePayload) => {
    try {
      setState((prevState) => ({ ...prevState, isFetching: true }));
      await contactRepository.requestService(payload);
      AlertManager.success(messages.FINSIGHT_PRICING_REQUEST_SERVICE_SUCCESS);
      closeModal();
    } catch (error) {
      AlertManager.error(getErrorMessage(error));
    } finally {
      setState((prevState) => ({ ...prevState, isFetching: false }));
    }
  };

  const onSubmit = async (data: IRequestServiceData) => {
    const payload: IRequestServicePayload = {
      firstName: data.firstName,
      lastName: data.lastName,
      companyName: data.companyName,
      corpEmail: data.email,
      phone: data.phone.value,
      serviceType: state.serviceType,
      comment: data.comment,
      token: null,
    };

    const additionalServices: ServiceType[] = [];

    if (data.creditFlowApi) {
      additionalServices.push(ServiceType.API);
    }

    if (data.officeAddInExcel) {
      additionalServices.push(ServiceType.BONDSCREENER_EXPORT);
    }

    if (additionalServices.length) {
      payload.additionalServices = additionalServices;
    }

    if (!state.isCaptcha) {
      await requestService(payload);
    } else if (state.recaptcha.getValue()) {
      payload.token = state.recaptcha.getValue();
      await requestService(payload);
    } else {
      payload.token = await state.recaptcha.executeAsync();
      await requestService(payload);
    }
  };

  return {
    ...state,
    openModal,
    closeModal,
    setRecaptchaObject,
    onSubmit,
  };
};

type RequestServiceModalContextType = ReturnType<typeof useRequestServiceModal>;

export const RequestServiceModalContext = createContext<RequestServiceModalContextType>(null);

export const useRequestServiceModalContext = () => {
  const context = useContext<RequestServiceModalContextType>(RequestServiceModalContext);

  if (!context) {
    throw new Error('useRequestServiceModalContext must be used within a RequestServiceModalContext');
  }

  return context;
};

interface IProps {
  children: ReactNode,
}

const RequestServiceModalProvider = ({ children }: IProps) => (
  <RequestServiceModalContext.Provider value={ useRequestServiceModal() }>
    { children }
  </RequestServiceModalContext.Provider>
);

export default RequestServiceModalProvider;
